import { Loading } from '@src/common/Loading';
import { styled } from '@uhg-abyss/web/tools/styled';
import React, { useState } from 'react';
import { Card } from '@uhg-abyss/web/ui/Card';
import { Grid } from '@uhg-abyss/web/ui/Grid';
import { Divider } from '@uhg-abyss/web/ui/Divider';
import { Button } from '@uhg-abyss/web/ui/Button';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { IconReview } from '@src/common/Icon/IconReview';
import { IconCheckedCircle } from '@src/common/Icon/IconCheckedCircle';
import { IconLaptop } from '@src/common/Icon/IconLaptop';
import { Link } from '@uhg-abyss/web/ui/Link';
import { config } from '@uhg-abyss/web/tools/config';
import { useTinRegistrationStatus } from '@src/state/tinRegistrationStatus';
import { IconInventory } from '@src/common/Icon/IconInventory';
import { print } from './print';

const TextStyleBold = styled('div', {
  fontWeight: 'bold',
});

const HeaderTinStyle = styled('div', {
  fontWeight: 'bold',
  paddingLeft: '5px',
});

const CompleteStatusText = styled('div', {
  color: '$success1',
});

const NotEligibleStatusText = styled('div', {
  color: '$error1',
});

const InProgressStatusText = styled('div', {
  color: '$info1',
});

const GrayText = styled('div', {
  color: '$gray6',
});

const CardHeader = styled('div', {
  display: 'inherit',
});

const IconAlignment = styled('div', {
  display: 'inline-block',
  border: '1px solid green',
  backgroundColor: 'green',
  borderRadius: '100%',
  width: '18%',
});

const MarginStyle = styled('div', {
  margin: '10px 10px 10px 0.825rem',
});

const DownloadPdf = styled('div', {
  fontWeight: 500,
  margin: '10px 10px 10px 0.825rem',
  display: 'inline-flex',
});

const FlexStages = styled('div', {
  textAlign: 'center',
});

const CardWrapper = styled('div', {
  paddingBottom: '28px',
});

const IconStyles = styled('div', {
  textAlign: 'center',
});

export const TinStatus = () => {
  const statusInfo = useTinRegistrationStatus.state();
  const {
    tin,
    registrationStatus,
    trackingNumber,
    reviewCompletedOn,
    submittedOn,
  } = statusInfo.tinRegistrationStatus;
  const { searchRegistrationStatusSaga } = statusInfo;

  const [downloading, setDownloading] = useState(false);

  const downloadPdf = () => {
    const fileName = `TinRegistrationStatus_${trackingNumber}`;
    const statusPage = document.getElementById('print-status');
    setDownloading(true);
    print.savePDF(statusPage, fileName, () => {
      setDownloading(false);
    });
  };

  return (
    <Loading isLoading={searchRegistrationStatusSaga.isLoading}>
      <CardWrapper id="print-status">
        <Card
          header={
            <CardHeader id="Status-CardTitle">
              Taxpayer Identification Number:{' '}
              <HeaderTinStyle id="Status-CardTitle-tin">{tin}</HeaderTinStyle>
            </CardHeader>
          }
        >
          <Grid>
            <Grid.Col span="18%">
              <Grid css={{ padding: '23px', paddingRight: '5px' }}>
                <MarginStyle>
                  <GrayText id="Status-ReferenceNumberText">
                    Reference number
                  </GrayText>
                  <TextStyleBold id="Status-ReferenceNumber">
                    {trackingNumber}
                  </TextStyleBold>
                </MarginStyle>
                <MarginStyle>
                  <GrayText id="Status-SubDateText">Submission date</GrayText>
                  <TextStyleBold id="Status-SubDate">
                    {submittedOn}
                  </TextStyleBold>
                </MarginStyle>
                <DownloadPdf>
                  <Button
                    variant="tertiary"
                    isLoading={downloading}
                    onClick={downloadPdf}
                    id="Status-downloadButton"
                    css={{ padding: '0px' }}
                    before={
                      <IconSymbol
                        icon="download_2"
                        size="20px"
                        id="Status-downloadIcon"
                      />
                    }
                  >
                    Download Status
                  </Button>
                </DownloadPdf>
              </Grid>
            </Grid.Col>
            <Grid.Col span="2%">
              <Divider orientation="vertical" />
            </Grid.Col>
            <Grid.Col span="78%" css={{ paddingTop: '30px' }}>
              <Grid>
                <Grid.Col span="25%">
                  <IconStyles>
                    <IconCheckedCircle size="40" id="Status-subChecked" />
                  </IconStyles>
                </Grid.Col>
                <Grid.Col span="25%">
                  <IconStyles>
                    {registrationStatus !== 'PENDING' ? (
                      <IconCheckedCircle size="40" id="Status-reviewChecked" />
                    ) : (
                      <IconReview size="40" id="Status-review" />
                    )}
                  </IconStyles>
                </Grid.Col>
                {(registrationStatus === 'ACCEPTED' ||
                  registrationStatus === 'RELEASED') && (
                  <Grid.Col span="25%">
                    <IconStyles>
                      <IconAlignment>
                        <IconInventory size="34" id="Status-ClaimSubmitted" />
                      </IconAlignment>
                    </IconStyles>
                  </Grid.Col>
                )}
                <Grid.Col span="25%">
                  <IconStyles>
                    {registrationStatus === 'ACCEPTED' ? (
                      <IconLaptop size="40" id="Status-accessEligible" />
                    ) : registrationStatus === 'DENIED' ? (
                      <IconSymbol
                        icon="lock"
                        size={44}
                        variant="outlined"
                        color="gray"
                        id="Status-accessLockedNotEligible"
                      />
                    ) : (
                      <IconSymbol
                        icon="lock"
                        size={44}
                        variant="outlined"
                        id="Status-accessLocked"
                      />
                    )}
                  </IconStyles>
                </Grid.Col>
              </Grid>
              <Grid>
                <Grid.Col span="25%">
                  <FlexStages>
                    <TextStyleBold id="Status-SubStageLabel">
                      TIN registration submission
                    </TextStyleBold>
                    <CompleteStatusText id="Status-SubStageStatus">
                      Complete
                    </CompleteStatusText>
                  </FlexStages>
                </Grid.Col>
                <Grid.Col span="25%">
                  {registrationStatus !== 'PENDING' ? (
                    <FlexStages>
                      <TextStyleBold id="Status-ReviewStageLabel">
                        Review
                      </TextStyleBold>
                      <CompleteStatusText id="Status-ReviewStageStatus">
                        Complete
                      </CompleteStatusText>
                      <GrayText id="Status-ReviewStageCompletedOn">
                        Completed on {reviewCompletedOn}
                      </GrayText>
                    </FlexStages>
                  ) : (
                    <FlexStages>
                      <TextStyleBold id="Status-ReviewStageLabel">
                        Review
                      </TextStyleBold>
                      <InProgressStatusText id="Status-ReviewStageStatus">
                        In progress
                      </InProgressStatusText>
                      <GrayText id="Status-PortalReviewStatus">
                        Correspondence will be sent to you when the review is
                        complete
                      </GrayText>
                    </FlexStages>
                  )}
                </Grid.Col>
                {registrationStatus === 'ACCEPTED' && (
                  <Grid.Col span="25%">
                    <FlexStages>
                      <TextStyleBold id="Status-PortalStageLabel">
                        Claim submitted
                      </TextStyleBold>
                      <CompleteStatusText id="Status-PortalStageStatus">
                        Complete
                      </CompleteStatusText>
                      <GrayText id="Status-PortalClaimStatus">
                        Provider portal is available
                      </GrayText>
                    </FlexStages>
                  </Grid.Col>
                )}
                {registrationStatus === 'RELEASED' && (
                  <Grid.Col span="25%">
                    <FlexStages>
                      <TextStyleBold id="Status-PortalStageSubmitClaim">
                        Submit claim to UHC
                      </TextStyleBold>
                      <CompleteStatusText id="Status-PortalStatusAvailable">
                        Available
                      </CompleteStatusText>
                      <GrayText id="Status-PortalSubmitClaimStatus">
                        Submit claim for portal access
                      </GrayText>
                    </FlexStages>
                  </Grid.Col>
                )}
                <Grid.Col span="25%">
                  {registrationStatus === 'ACCEPTED' ? (
                    <FlexStages>
                      <TextStyleBold id="Status-PortalStageLabel">
                        UnitedHealthcare <br /> Provider Portal access
                      </TextStyleBold>
                      <CompleteStatusText id="Status-PortalStageStatus">
                        Available now
                      </CompleteStatusText>
                      <Link
                        id="Status-PortalUrl"
                        href={config('PORTAL_REGISTRATION_URL')}
                      >
                        Register Now
                      </Link>
                    </FlexStages>
                  ) : registrationStatus === 'DENIED' ? (
                    <FlexStages>
                      <TextStyleBold id="Status-PortalStageLabel">
                        UnitedHealthcare <br /> Provider Portal access
                      </TextStyleBold>
                      <NotEligibleStatusText id="Status-PortalStageStatus">
                        Not eligible
                      </NotEligibleStatusText>
                    </FlexStages>
                  ) : (
                    <FlexStages>
                      <TextStyleBold id="Status-PortalStageLabel">
                        UnitedHealthcare <br /> Provider Portal access
                      </TextStyleBold>
                      <GrayText id="Status-PortalStageStatus">
                        Not available yet
                      </GrayText>
                    </FlexStages>
                  )}
                </Grid.Col>
              </Grid>
            </Grid.Col>
          </Grid>
        </Card>
      </CardWrapper>
    </Loading>
  );
};
